import React from "react";
import { ArrowRight, Compass, ShieldCheck } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

/**
 * Phase 11 — editorial Hero. Left-side copy + right-side SVG life-journey card.
 * No framer-motion on the SVG card — uses CSS-only `.arc-draw` + fade-in utilities
 * so it reliably paints on first render across browsers.
 */
export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 sm:pt-32 lg:pt-36 pb-20 sm:pb-24 overflow-hidden hero-grain bg-lw-ivory"
    >
      <div
        aria-hidden="true"
        className="absolute top-0 right-0 w-[60%] h-[80%] pointer-events-none opacity-70"
        style={{
          background:
            "radial-gradient(ellipse at top right, rgba(212,175,55,0.16) 0%, rgba(212,175,55,0) 60%)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-24 -left-24 w-[420px] h-[420px] pointer-events-none opacity-40"
        style={{
          background:
            "radial-gradient(circle, rgba(10,37,64,0.10) 0%, rgba(10,37,64,0) 70%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {/* LEFT — copy */}
        <div className="lg:col-span-7 reveal visible">
          <div className="flex items-center gap-3 mb-7">
            <span className="lw-eyebrow">Private Wealth · Generational</span>
            <span aria-hidden="true" className="h-px w-10 bg-lw-gold/60" />
          </div>

          <h1
            className="font-serif-display text-4xl sm:text-5xl lg:text-6xl xl:text-[4.25rem] leading-[1.03] text-lw-navy"
            data-testid="hero-headline"
          >
            From Childhood. <br className="hidden sm:block" />
            Through Life.{" "}
            <span className="italic text-lw-gold">Into Legacy.</span>
          </h1>

          <p
            className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-lw-muted"
            data-testid="hero-subhead"
          >
            Financial planning that grows with your family — across every
            stage of life. Independent guidance, education-first, designed
            to be transparent about what each product can and cannot do.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-3 sm:gap-4">
            <a
              href="/tools/recommender"
              data-testid="hero-cta-start-plan"
              className="btn-gold"
              onClick={() =>
                trackEvent("hero_cta_click", { target: "start_plan" })
              }
            >
              Start your free plan <ArrowRight size={18} />
            </a>
            <a
              href="/apply"
              data-testid="hero-cta-talk-advisor"
              className="btn-ghost-navy"
              onClick={() =>
                trackEvent("hero_cta_click", { target: "talk_to_advisor" })
              }
            >
              <Compass size={18} /> Talk to an advisor
            </a>
          </div>

          <div className="mt-10 flex items-center gap-5 text-xs sm:text-sm text-lw-muted">
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} className="text-lw-gold" />
              <span>Independent · Multiple carriers</span>
            </div>
            <div aria-hidden="true" className="h-4 w-px bg-lw-navy/15" />
            <span data-testid="hero-license-chip">
              Licensed in select states — see Compliance
            </span>
          </div>
        </div>

        {/* RIGHT — SVG life-journey card */}
        <div className="lg:col-span-5">
          <div className="relative">
            <div
              aria-hidden="true"
              className="absolute -inset-4 sm:-inset-6 rounded-[2rem]"
              style={{
                background:
                  "linear-gradient(135deg, rgba(10,37,64,0.08) 0%, rgba(212,175,55,0.08) 100%)",
                filter: "blur(28px)",
              }}
            />
            <div className="relative rounded-[1.75rem] overflow-hidden border border-lw-gold/40 shadow-[0_30px_80px_-30px_rgba(10,37,64,0.35)]">
              <div style={{ background: "#0A2540" }}>
                <JourneyCurveSVG />
              </div>
              <div className="gold-hairline" />
              <div className="px-6 py-5" style={{ background: "#061730" }}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-lw-gold" />
                  <span className="font-sans-ui text-[0.65rem] tracking-[0.22em] uppercase text-lw-gold">
                    The Lifespan Arc
                  </span>
                </div>
                <p className="font-serif-display text-white text-lg leading-snug">
                  One plan. Five life stages. A lifetime of clarity.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const STAGE_LABELS = ["Childhood", "Career Start", "Growing Family", "Pre-Retirement", "Legacy"];

const JourneyCurveSVG = () => {
  const nodes = [
    { x: 60, y: 230 },
    { x: 180, y: 130 },
    { x: 310, y: 80 },
    { x: 440, y: 130 },
    { x: 580, y: 230 },
  ];

  return (
    <svg
      viewBox="0 0 640 340"
      className="w-full h-auto block"
      aria-label="Life journey — five stages rising"
      role="img"
      preserveAspectRatio="xMidYMid meet"
      style={{ display: "block" }}
    >
      <defs>
        <linearGradient id="hero-arc-grad" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.4" />
          <stop offset="50%" stopColor="#E8C85C" stopOpacity="1" />
          <stop offset="100%" stopColor="#D4AF37" stopOpacity="0.4" />
        </linearGradient>
        <radialGradient id="hero-glow" cx="50%" cy="30%" r="80%">
          <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.14" />
          <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect x="0" y="0" width="640" height="340" fill="#0A2540" />
      <rect x="0" y="0" width="640" height="340" fill="url(#hero-glow)" />
      <line
        x1="30"
        y1="290"
        x2="610"
        y2="290"
        stroke="#D4AF37"
        strokeOpacity="0.18"
        strokeWidth="1"
      />

      {/* Green bars behind nodes */}
      {nodes.map((n, i) => (
        <rect
          key={`bar-${i}`}
          x={n.x - 18}
          y={n.y + 24}
          width="36"
          height={290 - (n.y + 24)}
          rx="3"
          fill="#2E7D32"
          fillOpacity="0.55"
        />
      ))}

      {/* Gold arc with CSS draw-in animation */}
      <path
        className="arc-draw"
        style={{ "--lwp-draw-length": "1200" }}
        d="M 30 270 C 170 20, 470 20, 610 270"
        stroke="url(#hero-arc-grad)"
        strokeWidth="4"
        fill="none"
        strokeLinecap="round"
      />

      {/* Stage markers */}
      {nodes.map((n, i) => (
        <g key={`node-${i}`}>
          <circle cx={n.x} cy={n.y} r="24" fill="#FAF7F2" stroke="#D4AF37" strokeWidth="2.5" />
          <circle cx={n.x} cy={n.y - 5} r="5.5" fill="#0A2540" />
          <path
            d={`M ${n.x - 10} ${n.y + 10} C ${n.x - 10} ${n.y + 2}, ${n.x + 10} ${n.y + 2}, ${n.x + 10} ${n.y + 10}`}
            fill="#0A2540"
          />
          <text
            x={n.x}
            y={n.y + 48}
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
            fontSize="11"
            fontWeight="500"
            fill="#D4AF37"
          >
            {STAGE_LABELS[i]}
          </text>
        </g>
      ))}
    </svg>
  );
};

export default Hero;
